import React from 'react';
import classNames from 'classnames';
import { useSortBy, useTable, useExpanded, Column, Row } from 'react-table';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { BuildingSummaryHealthStatus } from '../../../../../models/buildingModels';
import ExpandedCustomerRow from '../../../../partner/components/ExpandedCustomerRow';
import { EmptyTable } from '../../../buildingStatus/components/common';

type SortedArrowProps = {
    isSortedDesc?: boolean;
};

const SortedArrow = ({ isSortedDesc }: SortedArrowProps): React.ReactElement =>
    isSortedDesc ? <MaterialIcon name="arrow_drop_up" /> : <MaterialIcon name="arrow_drop_down" />;

export type LocationRowData = {
    locationId: string;
    locationName: string;
    hubs: number;
    sensorDevices: number;
    display: {
        sensorDevices: string;
        hubs: string;
    };
};

export type CustomerRowData = {
    userGroupId: string;
    customerName: string;
    totalLocations: number;
    hubs: number;
    sensorDevices: number;
    buildings: number;
    offlineLocations: BuildingSummaryHealthStatus[];
    display: {
        buildings: string;
        sensorDevices: string;
        hubs: string;
    };
};

type Props = {
    data: LocationRowData[] | CustomerRowData[];
    columns: Column<LocationRowData | CustomerRowData>[];
    emptyTableText: string;
    styles: { [key: string]: string };
};

export default function HealthStatusTable({ data, columns, emptyTableText, styles }: Props): React.ReactElement {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<
        LocationRowData | CustomerRowData
    >(
        {
            columns,
            data,
        },
        useSortBy,
        useExpanded
    );

    const expandedTable = (row: Row<LocationRowData | CustomerRowData>): React.ReactElement => {
        const rowData = row.original as CustomerRowData;
        const { offlineLocations, userGroupId } = rowData;
        return <ExpandedCustomerRow offlineLocations={offlineLocations} partnerManagedUserGroupId={userGroupId} />;
    };

    return (
        <div {...getTableProps()} className={styles.table}>
            {headerGroups.map(headerGroup => {
                const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps({
                    className: styles.header,
                });
                return (
                    <div key={headerGroupKey} {...restHeaderGroupProps}>
                        {headerGroup.headers.map(column => {
                            const { key: headerKey, ...rest } = column.getHeaderProps([
                                {
                                    className: classNames(styles.cell, column.className),
                                },
                                column.getSortByToggleProps(),
                            ]);
                            return (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    key={headerKey}
                                    {...rest}
                                    onKeyUp={(e): void => {
                                        if (e.key === 'Enter') column.toggleSortBy();
                                    }}
                                >
                                    {column.isSorted ? <SortedArrow isSortedDesc={column.isSortedDesc} /> : <div />}
                                    <span>{column.render('Header')}</span>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            <div {...getTableBodyProps()}>
                {rows.length > 0 ? (
                    rows.map(row => {
                        prepareRow(row);
                        const { key: rowKey, ...restRowProps } = row.getRowProps();
                        return (
                            <div key={rowKey} {...restRowProps}>
                                <div className={styles.row}>
                                    {row.cells.map(({ getCellProps, render, column }) => {
                                        const { key: cellKey, ...restCellProps } = getCellProps({
                                            className: classNames(styles.cell, column.className),
                                        });
                                        return (
                                            <div key={cellKey} {...restCellProps}>
                                                <span>{render('Cell')}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {row.isExpanded ? expandedTable(row as Row<LocationRowData | CustomerRowData>) : null}
                            </div>
                        );
                    })
                ) : (
                    <EmptyTable emptyTableText={emptyTableText} />
                )}
            </div>
        </div>
    );
}
